import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material";

interface StoreRow {
  locationName: string;
  months: Record<string, number>;
  amounts: Record<string, number>;
  total: number;
  totalAmount: number;
}

interface SalesData {
  productId16: number | null;
  stores: StoreRow[];
  monthKeys: string[];
  grandTotal: number;
  grandTotalAmount: number;
}

interface Props {
  open: boolean;
  unifiedProductId: string | null;
  productName?: string | undefined;
  onClose: () => void;
}

/** Format YYYY-MM to short readable label, e.g. "Ene 26" */
function formatMonthLabel(key: string): string {
  const parts = key.split("-");
  const yearStr = parts[0] ?? "";
  const monthStr = parts[1] ?? "";
  const monthNames = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  const idx = parseInt(monthStr, 10) - 1;
  const shortYear = yearStr.slice(2);
  return `${monthNames[idx] ?? monthStr} ${shortYear}`;
}

export function SalesDetailsDialog({
  open,
  unifiedProductId,
  productName,
  onClose,
}: Props): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SalesData | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!open || !unifiedProductId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/forecasting/product-sales-by-store/${encodeURIComponent(unifiedProductId)}?months=4`,
        );
        const json = await res.json();
        if (json?.status === "success" && json.data) {
          if (active) setData(json.data as SalesData);
        } else {
          throw new Error("Respuesta invalida del servidor");
        }
      } catch (e) {
        if (active) setError("No se pudo cargar las ventas por tienda");
        console.error("Sales details fetch error:", e);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [open, unifiedProductId]);

  const monthKeys = useMemo(() => data?.monthKeys ?? [], [data]);
  const stores = useMemo(() => data?.stores ?? [], [data]);
  const totalsPerMonth = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const key of monthKeys) {
      totals[key] = stores.reduce((sum, s) => sum + (s.months[key] ?? 0), 0);
    }
    return totals;
  }, [stores, monthKeys]);
  const amountTotalsPerMonth = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const key of monthKeys) {
      totals[key] = stores.reduce((sum, s) => sum + (s.amounts?.[key] ?? 0), 0);
    }
    return totals;
  }, [stores, monthKeys]);

  const fmtCurrency = (v: number) =>
    "$" +
    v.toLocaleString("es-MX", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" component="div" fontWeight={600}>
            Ventas por tienda (ultimos 4 meses)
          </Typography>
        </Stack>
        {productName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {productName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 200 }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Cargando ventas...
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !data || stores.length === 0 ? (
          <Alert severity="info">
            Sin datos de ventas por tienda disponibles.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Typography variant="body2">
                Total ventas: <b>{fmtCurrency(data.grandTotalAmount ?? 0)}</b>
              </Typography>
              <Typography variant="body2">
                Total piezas: <b>{data.grandTotal.toLocaleString("es-MX")}</b>
              </Typography>
              <Typography variant="body2">
                Tiendas con ventas: <b>{stores.length}</b>
              </Typography>
            </Stack>
            <Divider />
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Tienda</TableCell>
                  {monthKeys.map((mk) => (
                    <TableCell key={mk} align="right" sx={{ fontWeight: 700 }}>
                      {formatMonthLabel(mk)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stores.map((store) => (
                  <TableRow key={store.locationName} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {store.locationName}
                      </Typography>
                    </TableCell>
                    {monthKeys.map((mk) => {
                      const val = store.months[mk] ?? 0;
                      const amt = store.amounts?.[mk] ?? 0;
                      return (
                        <TableCell key={mk} align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              color:
                                amt === 0 ? "text.disabled" : "text.primary",
                            }}
                          >
                            {amt > 0 ? fmtCurrency(amt) : "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              color:
                                val === 0 ? "text.disabled" : "text.secondary",
                              display: "block",
                            }}
                          >
                            {val > 0
                              ? val.toLocaleString("es-MX") + " pzas"
                              : ""}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {fmtCurrency(store.totalAmount ?? 0)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 600,
                          color: "text.secondary",
                          display: "block",
                        }}
                      >
                        {store.total.toLocaleString("es-MX")} pzas
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow
                  sx={{ "& td": { borderTop: 2, borderColor: "divider" } }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      Total
                    </Typography>
                  </TableCell>
                  {monthKeys.map((mk) => (
                    <TableCell key={mk} align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 700 }}
                      >
                        {fmtCurrency(amountTotalsPerMonth[mk] ?? 0)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          color: "text.secondary",
                          display: "block",
                        }}
                      >
                        {(totalsPerMonth[mk] ?? 0).toLocaleString("es-MX")} pzas
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontWeight: 700 }}
                    >
                      {fmtCurrency(data.grandTotalAmount ?? 0)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        color: "text.secondary",
                        display: "block",
                      }}
                    >
                      {data.grandTotal.toLocaleString("es-MX")} pzas
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          variant="contained"
          onClick={onClose}
          color="primary"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SalesDetailsDialog;
